import React, { useEffect, useState } from "react";
import {
  Users2,
  BarChart3,
  BookUser,
  TriangleAlert,
  Download,
  BookOpenText,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import axios from "axios";
import { subDays, format } from "date-fns";
import config from "../config";

const Dashboard = () => {
  const [stats, setStats] = useState({
    userCount: 0,
    affirmationCount: 0,
    devotionCount: 0,
    challengeCount: 0,
    downloadCount: 0,
    prayerGuideCount: 0,
    userSignupsLast30Days: [],
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${config.BASE_URL}/apis/stats`);
        if (res.data.success) {
          setStats(res.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch dashboard stats", err);
      }
    };

    fetchStats();
  }, []);

  // Generate last 30 days date strings
  const today = new Date();
  const past30Days = Array.from({ length: 30 }).map((_, i) => {
    const date = subDays(today, 29 - i);
    return format(date, "yyyy-MM-dd");
  });

  // Map signup data to date
  const signupMap = stats.userSignupsLast30Days.reduce((acc, entry) => {
    acc[entry.date] = parseInt(entry.count);
    return acc;
  }, {});

  // Final bar chart data with empty days filled
  const barData = past30Days.map(date => ({
    name: format(new Date(date), "dd"),
    value: signupMap[date] || 0,
    color: "#FE7B38",
  }));

  const totalNewUsers = barData.reduce((sum, d) => sum + d.value, 0);

  const statCards = [
    { label: "Users List", value: stats.userCount, icon: BarChart3 },
    { label: "Affirmations", value: stats.affirmationCount, icon: Users2 },
    { label: "Devotions", value: stats.devotionCount, icon: BookUser },
    { label: "Challenges", value: stats.challengeCount, icon: TriangleAlert },
    { label: "Download Resources", value: stats.downloadCount, icon: Download },
    { label: "Prayer Guides", value: stats.prayerGuideCount, icon: BookOpenText },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-8 space-y-8">
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {statCards.map(({ label, value, icon: Icon }) => (
          <div
            key={label}
            className="flex items-center gap-4 rounded-[6px] bg-white p-4 shadow hover:shadow-md transition-shadow"
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-orange-500 text-white">
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-lg text-gray-300">{label}</p>
              <p className="text-2xl font-semibold">{value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-[6px] bg-white p-6 shadow">
        <p className="mb-1 text-sm font-medium text-gray-500">
          New User Sign‑ups Overview
        </p>
        <div className="mb-4 text-3xl font-extrabold text-orange-500">{totalNewUsers}</div>
        <div className="h-80 w-full">
          <ResponsiveContainer>
            <BarChart data={barData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
              />
              <YAxis hide />
              <Tooltip cursor={{ fill: "transparent" }} />
              <ReferenceLine y={Math.max(...barData.map(d => d.value))} stroke="#ff7b3f" strokeDasharray="4 4" />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {barData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="mb-1 text-sm font-medium text-gray-500">
         Last 30 days 
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
